// src/features/signature/PartyDialog.tsx
// Create or edit a signing party. Create registers a party on a target
// entity; edit is a partial update (update_mask covers the editable fields).
// See docs/ux/signature-ia.md section 2.

import { useState } from 'react';
import { useI18n, useSignature } from '@react/hooks';
import { Button, Input, ErrorAlert } from '@react/components';
import { Select } from '@/components/Select';
import { PartyRoleValues, PartyNotificationChannelValues } from '@core/types/signature';
import type { Party, PartyRole, PartyNotificationChannel } from '@core/types/signature';
import { useScope } from '@/hooks/useScope';
import { enumSuffix } from './logic';

export function PartyDialog({ party, onSaved, onCancel }: { party?: Party; onSaved: () => void; onCancel: () => void }) {
  const { t } = useI18n();
  const store = useSignature();
  const scope = useScope();
  const isEdit = Boolean(party);

  const [targetType, setTargetType] = useState(party?.target?.type_name ?? 'document');
  const [targetId, setTargetId] = useState(party?.target?.id ?? '');
  const [role, setRole] = useState<PartyRole>(party?.role ?? 'PARTY_ROLE_CONTRACTOR');
  const [displayName, setDisplayName] = useState(party?.display_name ?? '');
  const [notificationChannel, setNotificationChannel] = useState<PartyNotificationChannel>(party?.notification_channel ?? 'PARTY_NOTIFICATION_CHANNEL_EMAIL');
  const [externalEmail, setExternalEmail] = useState(party?.external_email ?? '');
  const [externalPhone, setExternalPhone] = useState(party?.external_phone ?? '');
  const [signOrder, setSignOrder] = useState(String(party?.sign_order ?? 1));
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);
    try {
      if (isEdit && party?.entity?.id) {
        await store.UpdateParty(party.entity.id, {
          party: {
            ...party,
            role,
            display_name: displayName,
            notification_channel: notificationChannel,
            external_email: externalEmail,
            external_phone: externalPhone,
            sign_order: parseInt(signOrder, 10) || 0,
          },
          update_mask: ['role', 'display_name', 'notification_channel', 'external_email', 'external_phone', 'sign_order'],
        });
      } else {
        await store.CreateParty({
          party: {
            entity: { type_id: '', type_name: 'signature_party', id: '', platform_id: scope.platformId, tenant_id: scope.tenantId, product_id: scope.productId },
            target: { type_id: '', type_name: targetType.trim(), id: targetId.trim(), platform_id: scope.platformId, tenant_id: scope.tenantId, product_id: scope.productId },
            role,
            role_label: '',
            display_name: displayName,
            sign_order: parseInt(signOrder, 10) || 0,
            decline_reason: '',
            signature_method_allowed: [],
            notification_channel: notificationChannel,
            external_email: externalEmail,
            external_phone: externalPhone,
          },
        });
      }
      onSaved();
    } catch (err) {
      setError(err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3 rounded-lg border border-border p-4">
      <h3 className="text-sm font-semibold text-foreground">{isEdit ? t('signature.parties.editTitle') : t('signature.parties.createTitle')}</h3>
      {error != null && <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />}
      {!isEdit && (
        <div className="grid grid-cols-2 gap-3">
          <Input label={t('signature.verify.targetType')} value={targetType} onChange={(e) => setTargetType(e.target.value)} required />
          <Input label={t('signature.verify.targetId')} value={targetId} onChange={(e) => setTargetId(e.target.value)} required />
        </div>
      )}
      <Input label={t('signature.field.displayName')} value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
      <div className="grid grid-cols-2 gap-3">
        <Select
          label={t('signature.field.role')}
          value={role}
          onChange={(e) => setRole(e.target.value as PartyRole)}
          options={PartyRoleValues.filter((v) => v !== 'PARTY_ROLE_UNSPECIFIED').map((v) => ({ value: v, label: t(`signature.role.${enumSuffix(v, 'PARTY_ROLE_')}`) }))}
        />
        <Input label={t('signature.field.signOrder')} type="number" min={1} value={signOrder} onChange={(e) => setSignOrder(e.target.value)} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Input label={t('signature.field.externalEmail')} type="email" value={externalEmail} onChange={(e) => setExternalEmail(e.target.value)} />
        <Input label={t('signature.field.externalPhone')} value={externalPhone} onChange={(e) => setExternalPhone(e.target.value)} />
      </div>
      <Select
        label={t('signature.field.notificationChannel')}
        value={notificationChannel}
        onChange={(e) => setNotificationChannel(e.target.value as PartyNotificationChannel)}
        options={PartyNotificationChannelValues.filter((v) => v !== 'PARTY_NOTIFICATION_CHANNEL_UNSPECIFIED').map((v) => ({ value: v, label: t(`signature.notificationChannel.${enumSuffix(v, 'PARTY_NOTIFICATION_CHANNEL_')}`) }))}
      />
      <div className="flex items-center gap-2">
        <Button type="submit" size="sm" disabled={isSaving}>{isSaving ? t('common.saving') : t('common.save')}</Button>
        <Button type="button" variant="secondary" size="sm" onClick={onCancel} disabled={isSaving}>{t('common.cancel')}</Button>
      </div>
    </form>
  );
}
